import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { flattenOptions, IOption } from '@components/Filter/filter.utils';
import { FlexRow } from '@components/FlexRow';
import FloatingBar from '@components/FloatingBar';
import InputAutocomplete from '@components/InputAutocomplete';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import Colors from '@constants/Colors';
import { useOptionsContext } from '@modules/talents/context/options-context';
import { Box, Button, FormHelperText, Grid, IconButton, InputBase, MenuItem, Paper, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';
import useAttachFilesToPost from '../hooks/useAttachFilesToPost';
import { CREATE_POST, GET_POST_UPERCUT, GET_TOPICS, PIN_POST, PUBLISH_POST, UNPIN_POST, UNPUBLISH_POST, UPDATE_POST } from '../schema/blog';
import BlogHtmlEditor from './BlogHtmlEditor';
import BlogStatus from './BlogStatus';

type BlogFormInputs = {
    title: string;
    thumbnail?: File | null;
    html: string;
    summary: string;
    selectedTopic: any[];
    professsionIds: IOption[];
    isPublished: boolean;
    isDraft: boolean;
    role: "TALENT" | "PRO" | string;
    banner: {
        file?: any,
        _id?: string,
        url?: string
    }
};

const BlogCreateForm = () => {
    const { showSnackbar } = useSnackbar();

    const { data: optionsResult } = useOptionsContext();
    const { data: topicsData } = useQuery(GET_TOPICS);

    const { attachFilesToPost, progress } = useAttachFilesToPost();

    const { id: postId } = useParams();

    const quillRef = useRef<ReactQuill | null>(null);
    const navigate = useNavigate();
    const theme = useTheme();

    const thumbnailDefaultStyle = {
        border: '2px dashed #7B61FF',
        borderRadius: '16px',
        cursor: 'pointer',
        background: '#16133B',
        overflow: 'hidden',
        position: 'relative',
        boxShadow: '0px 4px 24px 0px rgba(123, 97, 255, 0.10)',
        transition: 'border-color 0.2s',
        width: '100%',
        aspectRatio: '16/9',
        minWidth: 320,
        minHeight: 180,
        maxWidth: 480,
        '&:hover': {
            borderColor: '#A992FF',
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }

    const thumbnailThemeStyle = {
        light: {
            ...thumbnailDefaultStyle,
            background: "#FBF9FF",
        },
        dark: {
            ...thumbnailDefaultStyle
        }
    }

    const [saveState, setSaveState] = useState<'saved' | 'unsaved' | 'empty'>("empty");

    const methods = useForm<BlogFormInputs>({
        defaultValues: {
            title: '',
            thumbnail: null,
            html: '',
            summary: '',
            selectedTopic: [],
            professsionIds: [],
            role: "",
            banner: {
                _id: null,
                file: null,
                url: null
            }
        },
    });
    const [fetchPost, { data }] = useLazyQuery(GET_POST_UPERCUT);
    const [createPost, { loading: creating }] = useMutation(CREATE_POST);
    const [updatePost, { loading: updating }] = useMutation(UPDATE_POST);
    const [publishPost, { loading: publishing }] = useMutation(PUBLISH_POST);
    const [unPublish, { loading: unpublishing }] = useMutation(UNPUBLISH_POST);
    const [pinPost, { loading: pinLoading }] = useMutation(PIN_POST);
    const [unPinPost, { loading: unpinLoading }] = useMutation(UNPIN_POST);


    const publishPostHandler = useCallback(async () => {
        try {
            publishPost({
                variables: {
                    _id: postId
                }
            })
        } catch (err) {
            console.log(err)
        }
    }, [publishPost, postId])

    const unPublishPostHandler = useCallback(async () => {
        try {
            unPublish({
                variables: {
                    _id: postId
                }
            })
        } catch (err) {
            console.log(err)
        }
    }, [unPublish, postId]);

    const pinPostHandler = useCallback(async () => {
        try {
            await pinPost({
                variables: {
                    _id: postId
                },
                updateQueries: {
                    GetPostsUpercut: (prev, { mutationResult }) => {
                        const updatedPost = mutationResult.data.pinPost;
                        return {
                            getPostsUpercut: {
                                ...prev.getPostsUpercut,
                                docs: prev.getPostsUpercut.docs.map((post: any) =>
                                    post._id === updatedPost._id ? updatedPost : post
                                )
                            }
                        };
                    }
                }
            });
            showSnackbar("Article épinglé avec succès", "success");
        } catch (error) {
            console.error(error);
            showSnackbar("Une erreur s'est produite lors de l'épinglage de l'article", "error");
        }
    }, [pinPost, postId, showSnackbar]);

    const unPinPostHandler = useCallback(async () => {
        try {
            await unPinPost({
                variables: {
                    _id: postId
                },
                updateQueries: {
                    GetPostsUpercut: (prev, { mutationResult }) => {
                        const updatedPost = mutationResult.data.unpinPost;
                        return {
                            getPostsUpercut: {
                                ...prev.getPostsUpercut,
                                docs: prev.getPostsUpercut.docs.map((post: any) =>
                                    post._id === updatedPost._id ? updatedPost : post
                                )
                            }
                        };
                    }
                }
            });
            showSnackbar("Article détaché avec succès", "success");
        } catch (error) {
            console.error(error);
            showSnackbar("Une erreur s'est produite lors du détachement de l'article", "error");
        }
    }, [unPinPost, postId, showSnackbar]);

    const onSubmit = async (data: BlogFormInputs) => {
        try {
            // Prepare input for mutation
            const input: any = {
                title: data.title,
                html: data.html || '',
                summary: data.summary || '',
                topics: data.selectedTopic?.map(item => item.label),
                professionIds: data.professsionIds?.map(item => item.value),
                role: data.role === "all" ? "" : data.role,
                banner: data.banner
            };

            if (data.thumbnail) {
                const fileIds = await attachFilesToPost(postId, [data.thumbnail]);
                if (fileIds && fileIds.length > 0) {
                    input.thumbnail = fileIds[0];
                }
            }

            if (postId) {
                await updatePost({
                    variables: {
                        _id: postId,
                        input,
                    },
                    updateQueries: {
                        GetPostUpercut: (prev, { mutationResult }) => {
                            const updatedPost = mutationResult.data.updatePost;
                            return {
                                getPostUpercut: {
                                    ...prev.getPostUpercut,
                                    ...updatedPost,
                                }
                            };
                        },
                        GetPostsUpercut: (prev, { mutationResult }) => {
                            const updatedPost = mutationResult.data.updatePost;
                            return {
                                getPostsUpercut: {
                                    ...prev.getPostsUpercut,
                                    docs: prev.getPostsUpercut.docs.map((post: any) =>
                                        post._id === updatedPost._id ? updatedPost : post
                                    )
                                }
                            };
                        }
                    }
                });
                showSnackbar("Article mis à jour avec succès", "success");
            } else {
                const { data } = await createPost({
                    variables: {
                        input,
                    },
                    updateQueries: {
                        GetPostsUpercut: (prev, { mutationResult }) => {
                            const newPost = mutationResult.data.createPost;
                            return {
                                getPostsUpercut: {
                                    ...prev.getPostsUpercut,
                                    docs: [...prev.getPostsUpercut.docs, newPost],
                                    totalDocs: prev.getPostsUpercut.totalDocs + 1
                                }
                            };
                        }
                    }
                });
                navigate(`/blog/edit/${data.createPost._id}`, {
                    state: {
                        data: data.createPost
                    }
                });
                showSnackbar("Article créé avec succès", "success");
            }
            setSaveState('saved');
        } catch (error) {
            console.error(error);
            showSnackbar("Une erreur s'est produite lors de la sauvegarde de l'article", "error");
        }
    };

    useEffect(() => {
        if (methods.formState.isDirty) {
            setSaveState('unsaved')
        }
    }, [methods.formState.isDirty])

    useEffect(() => {
        (async () => {
            if (!postId) return;
            const { data } = await fetchPost({
                variables: {
                    _id: postId
                }
            });

            methods.reset({
                title: data.getPostUpercut.title || '',
                thumbnail: null,
                html: data.getPostUpercut.html || '',
                summary: data.getPostUpercut.summary || '',
                selectedTopic: data.getPostUpercut.topics?.map((item: any) => ({
                    label: item.name,
                    value: item._id
                })) || [],
                professsionIds: data.getPostUpercut.professions?.map((item: any) => ({
                    value: item._id,
                    label: item.name,
                    groupLabel: item.groupName
                })) || [],
                isPublished: data.getPostUpercut.isPublished || false,
                // isDraft: data.getPostUpercut.isDraft || false,
                role: data.getPostUpercut.role === "all" ? "" : data.getPostUpercut.role || "PRO",
                banner: {
                    _id: data.getPostUpercut.banner?._id || undefined,
                    file: null,
                    url: data.getPostUpercut.banner?.url || undefined
                }
            });
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchPost, methods.reset, postId])

    const loading = creating || updating || unpublishing || publishing || pinLoading || unpinLoading;

    return (
        <FormProvider {...methods}>
            <Box style={{ padding: '20px', position: "relative", minHeight: '100vh', margin: 0, borderRadius: 0 }}>
                <FloatingBar
                    onBack={() => navigate('/blog')}
                    info={
                        <>
                            <BlogStatus isPublished={data?.getPostUpercut?.isPublished} isDraft={data?.getPostUpercut?.isDraft} />
                            {saveState === 'saved' && <Typography component="small" color={Colors.NEUTRAL_GRAY}>
                                (enregistré)
                            </Typography>}
                            {saveState === 'unsaved' && <Typography component="small" color={Colors.NEUTRAL_GRAY}>
                                (non enregistré)
                            </Typography>}
                        </>
                    }
                    buttons={
                        <>
                            <Button size="large" onClick={methods.handleSubmit(onSubmit)} variant="outlined" color="neutralWhite" disabled={loading}>
                                Enregistrer
                            </Button>
                            <Button
                                component={'a'}
                                target='_blank'
                                href={process.env.REACT_APP_PUBLIC_WEB_URI + (data?.getPostUpercut?.slug ? `/blog/${data?.getPostUpercut?.slug}${!data?.getPostUpercut?.isPublished ? '?preview=' + data?.getPostUpercut?._id : ''}` : '')}
                                size="large" variant="outlined" color="neutralWhite"
                                disabled={!postId || loading}>
                                Aperçu
                            </Button>
                            {
                                data?.getPostUpercut?.isPublished ? <Button
                                    size="large"
                                    onClick={unPublishPostHandler}
                                    variant="outlined"
                                    color="neutralWhite"
                                    disabled={!postId || loading}
                                >
                                    Dépublier
                                </Button> : <Button size="large" onClick={publishPostHandler} variant="contained" color="primary" disabled={!postId || loading}>
                                    Publier
                                </Button>
                            }
                            <Button
                                size="large"
                                onClick={data?.getPostUpercut?.isPinned ? unPinPostHandler : pinPostHandler}
                                variant="contained"
                                color="secondary"
                                disabled={!postId || loading || pinLoading}
                            >
                                {data?.getPostUpercut?.isPinned ? 'Détacher' : 'Épingler'}
                            </Button>
                        </>
                    }
                />
                <Grid container gap={4} px={2} maxWidth="1200px" margin="0 auto">
                    <Grid item xs={12} >
                        <FlexRow gap={2} flexDirection="row" sx={{ marginBottom: 2 }}>
                            <Box sx={{ flex: 1, flexGrow: 1 }}>
                                <InputAutocomplete
                                    multiple
                                    allowCreate
                                    name='selectedTopic'
                                    options={topicsData?.getTopics?.map(item => ({ label: item.name, value: item._id })) || []}
                                    renderInput={(params) => {
                                        return (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                label={"Thèmes"}
                                                InputLabelProps={{
                                                    sx: {
                                                        color: (theme) => `${theme.palette.mode === 'dark' ? theme.palette.secondary.main : '#242333'}`,
                                                        "&.Mui-focused": {
                                                            color: "#fff",
                                                        },
                                                    }
                                                }}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    sx: {
                                                        color: (theme) => `${theme.palette.mode === 'dark' ? theme.palette.secondary.main : '#242333'}`,
                                                        width: '100%',
                                                        borderRadius: "32px",
                                                        background: (theme) => theme.palette.disableFormBlack.main,
                                                        backdropFilter: "blur(2.799999952316284px)",
                                                        "& fieldset": {
                                                            border: "none",
                                                        },
                                                    }
                                                }}
                                            />
                                        );
                                    }}
                                    sx={{
                                        background: "transparent"
                                    }}
                                />
                            </Box>
                            <Box sx={{ flex: 1, flexGrow: 1 }}>
                                <Controller
                                    name="role"
                                    control={methods.control}
                                    render={({ field }) => (
                                        <TextField
                                            select
                                            label={"Profile"}
                                            fullWidth
                                            variant="outlined"
                                            {...field}
                                            InputLabelProps={{
                                                sx: {
                                                    color: (theme) => theme.palette.secondary.main,
                                                    "&.Mui-focused": {
                                                        color: "#fff",
                                                    },
                                                }
                                            }}
                                            InputProps={{
                                                sx: {
                                                    color: (theme) => theme.palette.mode === 'dark' ? theme.palette.secondary.main : '#939393',
                                                    fontWeight: 700,
                                                    borderRadius: "32px",
                                                    background: (theme) => theme.palette.disableFormBlack.main,
                                                    backdropFilter: "blur(2.799999952316284px)",
                                                    "& fieldset": {
                                                        border: "none",
                                                    },
                                                }
                                            }}
                                        >
                                            <MenuItem value="all">Tous</MenuItem>
                                            <MenuItem value="PRO">Pro</MenuItem>
                                            <MenuItem value="TALENT">Talent</MenuItem>
                                        </TextField>
                                    )}
                                />
                            </Box>

                            <Box sx={{ flex: 1, flexGrow: 1 }}>
                                <InputAutocomplete
                                    name="professsionIds"
                                    label="Profession"
                                    multiple
                                    groupBy={option => option.groupLabel}
                                    getOptionLabel={option => `${option.label}`}
                                    options={flattenOptions(optionsResult?.options?.groupProfessions)}
                                    renderInput={(params) => {
                                        return (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                label={"Professions"}
                                                InputLabelProps={{
                                                    sx: {
                                                        color: (theme) => `${theme.palette.mode === 'dark' ? theme.palette.secondary.main : '#242333'}`,
                                                        "&.Mui-focused": {
                                                            color: "#fff",
                                                        },
                                                    }
                                                }}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    sx: {
                                                        color: (theme) => `${theme.palette.mode === 'dark' ? theme.palette.secondary.main : '#242333'}`,
                                                        width: '100%',
                                                        borderRadius: "32px",
                                                        background: (theme) => theme.palette.disableFormBlack.main,
                                                        backdropFilter: "blur(2.799999952316284px)",
                                                        "& fieldset": {
                                                            border: "none",
                                                        },
                                                    }
                                                }}
                                            />
                                        );
                                    }}
                                    sx={{
                                        background: "transparent",
                                    }}
                                />
                            </Box>
                        </FlexRow>
                    </Grid>
                    <Grid item xs={8} >
                        <Stack spacing={2} sx={{ marginBottom: 2 }}>
                            <Controller
                                name="title"
                                control={methods.control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <InputBase
                                        placeholder='Titre ici...'
                                        autoComplete='off'
                                        sx={{
                                            width: '100%',
                                            color: (theme) => theme.palette.lightGrey.main,
                                            fontFamily: "var(--font-family-Brand, Lemon milk pro)",
                                            fontSize: "39px !important",
                                            fontStyle: "medium",
                                            fontWeight: 500,
                                            lineHeight: "48px !important",
                                            border: "none",
                                            boxShadow: "none",
                                            background: "transparent",
                                            "&::placeholder": {
                                                color: "var(--text-Disabled, #939393)",
                                            }
                                        }}
                                        {...field}
                                    />
                                )}
                            />
                            <Controller
                                name="summary"
                                control={methods.control}
                                rules={{
                                    required: false,
                                    maxLength: {
                                        value: 500,
                                        message: "Le résumé ne doit pas dépasser 500 caractères."
                                    }
                                }}
                                render={({ field, fieldState }) => (
                                    <Box>
                                        <InputBase
                                            placeholder="Ajoutez un résumé de l'article ici..."
                                            multiline
                                            inputProps={{ maxLength: 500 }}
                                            sx={{
                                                width: '100%',
                                                borderRadius: "var(--Radius-radius-rounded, 16px)",
                                                background: (theme) => theme.palette.disableFormBlack.main,
                                                padding: "12px 16px",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                gap: "10px",
                                                alignSelf: "stretch",
                                                color: "var(--text-Disabled, #939393)",
                                                fontFamily: "var(--font-family-Core, Plus Jakarta Sans)",
                                                fontSize: "var(--font-size-md, 16px)",
                                                fontStyle: "normal",
                                                fontWeight: 700,
                                                lineHeight: "var(--font-line-height-sm, 24px)",
                                                ...(fieldState.error && {
                                                    borderColor: 'error.main',
                                                    borderWidth: '1px',
                                                    borderStyle: 'solid'
                                                }),
                                                "&:before": {
                                                    content: "' '",
                                                    display: "block",
                                                    width: "3px",
                                                    alignSelf: "stretch",
                                                    background: "var(--Color-Primary-1000, #16133B)",
                                                }
                                            }}
                                            {...field}
                                        />
                                        {fieldState.error && (
                                            <FormHelperText error>
                                                {fieldState.error?.message}
                                            </FormHelperText>
                                        )}
                                    </Box>
                                )}
                            />
                            <Controller
                                name="html"
                                control={methods.control}
                                render={({ field }) => (
                                    <BlogHtmlEditor
                                        ref={quillRef}
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="Écrivez votre histoire ici..."
                                        uploadProgress={progress}
                                        onUploadFiles={async (files) => {
                                            // If postId is null, save as draft first
                                            let currentPostId = postId;
                                            if (!currentPostId) {
                                                // Save as draft
                                                const input: any = {
                                                    title: methods.getValues("title") || "(Draft)",
                                                    html: methods.getValues("html"),
                                                    summary: methods.getValues("summary"),
                                                    topics: methods.getValues("selectedTopic")?.map((item: any) => item.label),
                                                    professionIds: methods.getValues("professsionIds")?.map((item: any) => item.value),
                                                    role: methods.getValues("role") === "all" ? "" : methods.getValues("role"),
                                                    banner: methods.getValues("banner"),
                                                    isDraft: true,
                                                    isPublished: false,
                                                };
                                                const { data } = await createPost({
                                                    variables: { input },
                                                });
                                                currentPostId = data?.createPost?._id;
                                                if (currentPostId) {
                                                    navigate(`/blog/edit/${currentPostId}`, {
                                                        state: { data: data.createPost }
                                                    });
                                                }
                                            }
                                            // Now attach files to the (possibly newly created) post
                                            return await attachFilesToPost(currentPostId, files);
                                        }}
                                    />
                                )}
                            />
                        </Stack>
                    </Grid>
                    <Grid item sx={{ width: 320 }}>
                        <Controller
                            name="banner"
                            control={methods.control}
                            render={({ field }) => (
                                <label htmlFor="thumbnail-upload" style={{ width: '100%', display: 'block' }}>
                                    <input
                                        id="thumbnail-upload"
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            field.onChange({ file });
                                        }}
                                    />
                                    <Paper
                                        variant="outlined"
                                        sx={thumbnailThemeStyle[theme.palette.mode || "dark"]}
                                    >
                                        {(field.value?.file || field.value?.url) ? (
                                            <Stack sx={{ position: 'relative', width: '100%', height: '100%' }}>
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        height: '100%',
                                                        aspectRatio: '16/9',
                                                        position: 'relative',
                                                        minHeight: 180,
                                                        minWidth: 320,
                                                        maxWidth: 480,
                                                    }}
                                                >
                                                    <img
                                                        src={field.value.url || URL.createObjectURL(field.value.file)}
                                                        alt="Thumbnail Preview"
                                                        style={{
                                                            objectFit: 'cover',
                                                            borderRadius: '16px',
                                                            width: '100%',
                                                            height: '100%',
                                                            aspectRatio: '16/9',
                                                            display: 'block',
                                                        }}
                                                    />
                                                </Box>
                                                <IconButton
                                                    color="error"
                                                    size="small"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        zIndex: 2,
                                                        minWidth: 0,
                                                        fontSize: 16,
                                                    }}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        field.onChange({ file: undefined, url: undefined, _id: undefined });
                                                    }}
                                                >
                                                    <i className="fi fi-rr-cross"></i>
                                                </IconButton>
                                            </Stack>
                                        ) : (
                                            <Stack alignItems="center" spacing={1} padding={4} sx={{ width: '100%' }}>
                                                {/* Camera icon SVG */}
                                                <i className='fi fi-br-camera' style={{ fontSize: '24px', color: theme.palette.mode === 'dark' ? Colors.NEUTRAL_WHITE : Colors.PRIMARY_PURPLE }}></i>
                                                <span style={{
                                                    color: theme.palette.mode === 'dark' ? Colors.NEUTRAL_WHITE : Colors.NEUTRAL_BLACK,
                                                    fontWeight: 700,
                                                    fontSize: 18,
                                                    textAlign: 'center',
                                                    fontFamily: 'Plus Jakarta Sans, sans-serif'
                                                }}>
                                                    Ajouter une image miniature
                                                </span>
                                                <span style={{
                                                    color: '#B8B8D2',
                                                    fontWeight: 400,
                                                    fontSize: 14,
                                                    textAlign: 'center',
                                                    fontFamily: 'Plus Jakarta Sans, sans-serif'
                                                }}>
                                                    Recommandé&nbsp;: format JPG, PNG,<br />max 10&nbsp;Mo.
                                                </span>
                                            </Stack>
                                        )}
                                    </Paper>
                                </label>
                            )}
                        />
                    </Grid>
                </Grid>
            </Box>
        </FormProvider>
    );
};

export default BlogCreateForm;
